<script lang="ts">
  import LayoutGrid, { Cell } from '@smui/layout-grid';
  import { onDestroy, onMount } from 'svelte';
  import { getAppConfig } from '../../../common/config/app-config';
  import { getBlogDataByPagination } from '../services/blog-data-services';
  import { blogFilterHeading } from '../store/blog_store';
  import BlogCard from './BlogCard.svelte';
  import BlogCardSkeletonLoader from './BlogCardSkeletonLoader.svelte';
  import BlogCommonContent from './BlogCommonContent.svelte';

  export let blogFilterData;
  const { blogCategoryID, blogTotalResults } = getAppConfig();

  let page = 0;
  let scrollComponent;
  let blogDataLoaded: boolean = false;

  onMount(() => {
    observer.observe(scrollComponent);
  });

  onDestroy(() => {
    observer.disconnect();
  });
  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (blogFilterData.length < blogTotalResults) {
            fetchNextBlogData();
          } else if (blogFilterData.length > 0) {
            blogDataLoaded = true;
          }
          observer.disconnect();
        }
      });
    },
    {
      rootMargin: '35px 0px 35px 0px',
    }
  );

  /**
   * Fetches the next set of blog posts based on the current page number.
   * If data is successfully fetched, it will be appended to the existing blog filter data.
   * The scroll component will be re-observed for further scroll events.
   * @returns {Promise<void>} - A promise resolving to void
   */
  async function fetchNextBlogData() {
    // Increment the page number
    page++;

    try {
      // Fetch blog data
      const response = await getBlogDataByPagination(blogCategoryID, page);

      // Early return if no results
      const nextPostData = response?.results || [];

      if (nextPostData.length > 0) {
        // Append new data to the existing blog filter data
        blogFilterData.push(...nextPostData);

        // If there's a scroll component, observe it for further scroll events
        observer?.observe(scrollComponent);
      }

      // Flag that data is loaded
      blogDataLoaded = true;
    } catch (error) {
      console.error('Error fetching blog data:', error);

      // Make sure to set this to true even if there's an error
      blogDataLoaded = true;
    }
  }
</script>

{#if blogFilterData && blogFilterData.length}
  <BlogCommonContent>
    {#if !blogDataLoaded}
      <BlogCardSkeletonLoader />
    {:else}
      <section class="blog-card-section filter-page">
        <LayoutGrid>
          {#if $blogFilterHeading}
            <Cell class="" spanDevices={{ phone: 4, tablet: 8, desktop: 12 }}>
              <span class="blog-filter-heading">{$blogFilterHeading}</span>
            </Cell>
          {/if}
          {#each blogFilterData as blogCard}
            <Cell class="" spanDevices={{ phone: 2, tablet: 2, desktop: 4 }}>
              <BlogCard data={blogCard} />
            </Cell>
          {/each}
        </LayoutGrid>
      </section>
    {/if}
    <div bind:this={scrollComponent} style="width:0px" />
  </BlogCommonContent>
{/if}

<style lang="scss">
  @import '../../common/styles/colors.new.scss';
  .blog-card-section {
    display: flex;
    gap: 60px;
    flex-wrap: wrap;
    padding: 20px 0;

    @include desktop-max-width {
      gap: 15px;
      padding: 30px 0;
    }
  }
  .blog-filter-heading {
    font-size: 24px;
    line-height: 18px;
    color: $neutral-dark-01;
    font-weight: 700;
    text-transform: capitalize;
    padding: 0;
    @include desktop-max-width {
      font-size: 16px;
      line-height: 24px;
    }
  }
  :global {
    .mdc-layout-grid {
      padding: 0 24px;
      @include desktop-max-width {
        padding: 0 12px;
      }
    }
    .mdc-layout-grid__inner {
      gap: 60px;
      @include desktop-max-width {
        gap: 16px;
      }
    }
  }
</style>
